import React, { useContext, useEffect, useMemo } from 'react';
import { OneLPSsdkParamsContext } from '../../../onelps-sdk-params-context';
import PropTypes from 'prop-types';
import TextInput from '../TextInput';
import CTAButton from '../../buttons/CTAButton';
import RegFormStyles from '../../../css/components/forms/standard/oneStepForm.css';
import Terms from '../Terms';
import AlreadyRegisteredLink, { ALREADY_REGISTERED_LINK_POSITION } from '../../common/AlreadyRegisteredLink';
import { withInlineValidation } from '../../../helpers/validation/inline-validation';
import { COOKIE_NOTIFICATION_NAME } from '../../../helpers/cookie-overlay';
import { withIppButtons } from '../../../helpers/ipp-buttons';
import { EmailFieldContext } from '../../../state-management/contexts/regform-fields/email-field-context';
import { NicknameFieldContext } from '../../../state-management/contexts/regform-fields/nickname-field-context';
import { PasswordFieldContext } from '../../../state-management/contexts/regform-fields/password-field-context';
import { TermsCheckboxContext } from '../../../state-management/contexts/regform-checkboxes/terms-checkbox-context';
import ErrorMessage, { ERROR_MESSAGE_STYLES } from '../ErrorMessage';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../Store';
import IppButtons, { IPP_BUTTONS_STYLE } from '../../ipp/IppButtons';
import { cookieExist, getCookieValue } from '../../../helpers/browser-cookies';
import {
	showFirstErrorArray, showAllErrorsArray,
} from '../../../helpers/validation/common';
import MoreInfoLink from '../MoreInfoLink';
import RaceSelectStandard from '../race-select/RaceSelectStandard';
import FormHeadline from '../FormHeadline';
import TabbedForms from '../tabbed-forms/TabbedForms';

function OneStepForm({
	hasNickname = false,
	hasEmail = false,
	errDelay,
	onEmailChangeCallback, onNicknameChangeCallback,
	onPasswordChangeCallback = null,
	onPasswordWithoutNicknameChangeCallback = null,
	onTermsCheckedCallback,
	handlePlayClick,
	handleIppLinkClick,
	showNicknameLoadingSpinner,
	showPasswordLoadingSpinner,
	showEmailLoadingSpinner,
	showLoadingIndicator,
}) {
	const { t } = useTranslation('translation');

	const [nicknameState, nicknameDispatch] = useContext(NicknameFieldContext);
	const [emailState, emailDispatch] = useContext(EmailFieldContext);
	const [passwordState, passwordDispatch] = useContext(PasswordFieldContext);
	const [termsState, termsDispatch] = useContext(TermsCheckboxContext);

	const onelpsParams = useContext(OneLPSsdkParamsContext);
	const state = useContext(AppContext)[0];

	const hasTabForms = onelpsParams.loginForm.type === 'tab';

	// The order of the states in the argument of showFirstErrorArray
	// is the same as the order of the ErrorMessage components in OneStepForm.
	const showErrorMessage = onelpsParams.form.showOnlyFirstValidationError ?
		useMemo(
			() => showFirstErrorArray([nicknameState, emailState, passwordState, termsState]),
			[emailState, nicknameState, passwordState, termsState]
		) :
		useMemo(
			() => showAllErrorsArray([nicknameState, emailState, passwordState, termsState]),
			[nicknameState, emailState, passwordState, termsState]);

	useEffect(() => {
		if (!onelpsParams.form.showTermsCheckbox) {
			termsDispatch({ type: 'SET_TC_VALUE', payload: true });
		}
	}, []);

	const formSectionClassNames = `
		${RegFormStyles.formContainer}
		${RegFormStyles[onelpsParams.gameId]}`;

	const showRaceSelection = state.showRaceSelection;

	if (showRaceSelection && onelpsParams.raceSelection.show) {
		return (
			<RaceSelectStandard
				fieldStates={{ nicknameState, passwordState, emailState, termsState }}
				fieldDispatches={{ nicknameDispatch, passwordDispatch, emailDispatch, termsDispatch }}
			/>
		);
	}

	const regForm = (
		<form
			className={RegFormStyles.form}
			onKeyDown={(e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					handlePlayClick();
				}
			}}
		>
			<main className={RegFormStyles.formMain}>
				{hasNickname && (
					<div className={RegFormStyles.fieldWrapper}>
						<TextInput
							type="text"
							id="nickname-input"
							labelText={t('form.username.label')}
							extraMargin
							onChangeCallback={onNicknameChangeCallback}
							hintMessagesList={[t('form.username.hint1'), t('form.username.hint2')]}
							value={nicknameState.value}
							valid={nicknameState.valid}
							showLoadingSpinner={showNicknameLoadingSpinner}
							disabled={showLoadingIndicator}
							autoComplete="new-password"
						/>
						{showErrorMessage[0] && (
							<div className={RegFormStyles.errorWrapper}>
								<ErrorMessage
									text={nicknameState.error.message}
									errorDelay={errDelay}
									style={ERROR_MESSAGE_STYLES.STANDARD}
									suggestions={nicknameState.suggestions}
									onSuggestionClick={(suggestedName) => {
										nicknameDispatch({ type: 'SET_NICKNAME_VALUE', payload: suggestedName });
										nicknameDispatch({ type: 'SET_NICKNAME_ERROR', payload: null });
										nicknameDispatch({ type: 'SET_NICKNAME_VALIDITY', payload: true });
									}}
								/>
							</div>
						)}
					</div>
				)}
				{hasEmail && (
					<div className={RegFormStyles.fieldWrapper}>
						<TextInput
							type="email"
							id="page_registration_step-one_input_email"
							labelText={t('form.email.label')}
							onChangeCallback={onEmailChangeCallback}
							hintMessagesList={[t('form.email.hint1')]}
							value={emailState.value}
							valid={emailState.valid}
							showLoadingSpinner={showEmailLoadingSpinner}
							disabled={showLoadingIndicator}
							autoComplete="new-password"
						/>
						{showErrorMessage[1] && (
							<div className={RegFormStyles.errorWrapper}>
								<ErrorMessage
									text={emailState.error.message}
									errorDelay={errDelay}
									style={ERROR_MESSAGE_STYLES.STANDARD}
								/>
							</div>
						)}
					</div>
				)}
				<div className={RegFormStyles.fieldWrapper}>
					<TextInput
						type="password"
						id="page_registration_step-one_input_password"
						labelText={t('form.password.label')}
						onChangeCallback={hasNickname ? onPasswordChangeCallback : onPasswordWithoutNicknameChangeCallback}
						hintMessagesList={[t('form.password.hint1'), t('form.password.hint2')]}
						value={passwordState.value}
						valid={passwordState.valid}
						showLoadingSpinner={showPasswordLoadingSpinner}
						disabled={showLoadingIndicator}
						autoComplete="new-password"
					/>
					{showErrorMessage[2] && (
						<div className={RegFormStyles.errorWrapper}>
							<ErrorMessage
								text={passwordState.error.message}
								errorDelay={errDelay}
								style={ERROR_MESSAGE_STYLES.STANDARD}
							/>
						</div>
					)}
				</div>
				<div className={RegFormStyles.fieldWrapper}>
					<Terms
						onChangeCallback={onTermsCheckedCallback}
						showCheckbox={onelpsParams.form.showTermsCheckbox}
						termsLinkId="page_registration_step-one_text_terms-and-conditions"
						privacyLinkId="page_registration_step-one_text_privacy-policy"
					>
					</Terms>
					{showErrorMessage[3] && (
						<div className={`${RegFormStyles.errorWrapper} ${RegFormStyles.terms}`}>
							<ErrorMessage
								text={termsState.error.message}
								style={ERROR_MESSAGE_STYLES.STANDARD}
							/>
						</div>
					)}
				</div>
				<div className={RegFormStyles.ctaButtonWrapper}>
					<CTAButton
						onButtonClick={handlePlayClick}
						buttonLabel={t(onelpsParams.form.buttonTranslationKeys[0])}
						buttonId="page_registration_step-one_button_register"
						isDisabled={showLoadingIndicator}
					/>
				</div>
			</main>

			<footer className={RegFormStyles.formFooter}>
				{onelpsParams.ippLinks.length > 0 && (
					<div className={RegFormStyles.ippWrapper}>
						<IppButtons
							captionText={t(`oneStepForm.ipp.caption.${onelpsParams.gameId}`)}
							style={IPP_BUTTONS_STYLE.MAIN_FORM}
							cookieNotificationValue={cookieExist(COOKIE_NOTIFICATION_NAME) ? getCookieValue(COOKIE_NOTIFICATION_NAME) : ''}
							onLinkClick={(e, link) => handleIppLinkClick(e, link, false)}
							captionPosition={onelpsParams.form.ippCaptionPosition}
						/>
						<div className={RegFormStyles.ippDivider}></div>
					</div>
				)}
				{onelpsParams.form.showAlreadyRegisteredLink && (
					<div className={RegFormStyles.alreadyRegisteredWrapper}>
						<AlreadyRegisteredLink
							linkText={t('loginForm.alreadyRegistered')}
							position={ALREADY_REGISTERED_LINK_POSITION.FORM}
						/>
					</div>
				)}
				{onelpsParams.form.showMoreInfoLink && (
					<div className={RegFormStyles.moreInfoLinkWrapper}>
						<MoreInfoLink />
					</div>
				)}
			</footer>
		</form>
	);

	return (
		<>
			<section className={formSectionClassNames}>
				{onelpsParams.form.showHeadline && (
					<header className={RegFormStyles.formHeader}>
						<FormHeadline text={t(`oneStepForm.header.${onelpsParams.gameId}`)} />
					</header>
				)}
				{hasTabForms ? (
					<TabbedForms>
						{regForm}
					</TabbedForms>
				) : regForm
				}
			</section>
		</>
	);
}

OneStepForm.propTypes = {
	hasNickname: PropTypes.bool,
	hasEmail: PropTypes.bool,
	errDelay: PropTypes.number.isRequired,
	onNicknameChangeCallback: PropTypes.func.isRequired,
	onEmailChangeCallback: PropTypes.func.isRequired,
	onPasswordChangeCallback: PropTypes.func,
	onPasswordWithoutNicknameChangeCallback: PropTypes.func,
	handlePlayClick: PropTypes.func.isRequired,
	onTermsCheckedCallback: PropTypes.func.isRequired,
	handleIppLinkClick: PropTypes.func.isRequired,
	showNicknameLoadingSpinner: PropTypes.bool.isRequired,
	showPasswordLoadingSpinner: PropTypes.bool.isRequired,
	showEmailLoadingSpinner: PropTypes.bool.isRequired,
	showLoadingIndicator: PropTypes.bool.isRequired,
};

const EnhancedOneStepForm =
	withIppButtons(
		withInlineValidation(
			OneStepForm
		)
	);
export default EnhancedOneStepForm;
